import GatewayAuthenticatorBase from './GatewayAuthenticatorBase';
import GatewayAdvancedAuthenticator from './GatewayAdvancedAuthenticator';
import GatewayBasicAuthenticator from './GatewayBasicAuthenticator';
import {
	AuthenticationData,
	GatewaySessionStatus,
	LogonResult,
	GatewayConstants,
} from './Definitions';
import { HTTPFetchResponse, HeadersJson } from '../dsAuth/data/AuthTypes';
import DSAuth from '../dsAuth/DSAuth';
import { gatewayConstants } from '../AuthConstants';
import { HttpStatusCodes } from '../../utils/http/HttpConstants';
import { Logger } from '../Logger';
import { AotCategory, logConstants } from '../../utils/Constants';
import {
	translateUrlGatewayToSF,
	translateUrlSFToGateway,
} from './GatewayURLTranslation';
import { GatewaySessionError } from '../AuthErrors';
import { ServerInfo, GatewayInfo } from '../../Common/Definitions';
import { getGatewayPasswordCountAndSelfDeclaredAuthType } from '../../AccountConfig/core/servertype/ServerType';
import { CookieManager } from '../CookieManager';
import { HeaderParser } from '../dsAuth/parser/HeaderParser';
import { UrlTranslationType } from '../main';
import { StoreType } from '../../Common/TypeDefinitions';

/**
 * @class - class which holds information about a gateway session
 * @member gateway - object of GatewayAuthenticatorBase, can be either basic or advanced
 * @member gatewayUrl - URL of the gateway
 * @member directUrl - Storefront URL
 * @member referenceUri - Reference URI of the account added
 * @member authenticationData - used to store cookies and other data related to the gateway session
 * @member status - current state of the gateway session
 */
export default class GatewaySession {
	private gateway: GatewayAuthenticatorBase;
	public gatewayUrl: URL;
	public directUrl: URL;
	private referenceUri: string;
	private authenticationData: AuthenticationData;
	private status: GatewaySessionStatus;
	private roamingAccountsResponse: HTTPFetchResponse;
	readonly serverType: StoreType;

	constructor(serverUrl: URL, probeResponse: HTTPFetchResponse, serverInfo: ServerInfo) {
		if (serverInfo.serverType === StoreType.AdvancedGateway) {
			Logger.getInstance().write(
				logConstants.INFO,
				`Gateway type - advanced authentication`
			);
			const formsStartPath: string = HeaderParser.getInstance().extractHeaderValue(
				probeResponse.headers,
				GatewayConstants.FormsStartHeader
			);
			this.gateway = new GatewayAdvancedAuthenticator(serverUrl, formsStartPath);
			this.serverType = StoreType.AdvancedGateway;
		} else if (serverInfo.serverType === StoreType.BasicGateway) {
			Logger.getInstance().write(
				logConstants.INFO,
				`Gateway type - basic authentication`
			);
			this.gateway = new GatewayBasicAuthenticator(serverUrl, serverInfo);
			this.serverType = StoreType.BasicGateway;
		} else {
			throw new GatewaySessionError(LogonResult.Aborted);
		}
		this.status = GatewaySessionStatus.NOT_INITIALIZED;
		this.gatewayUrl = serverUrl;
	}

	/**
	 * Static async function which probes for forms and calls the constructor of GatewaySession using the same response
	 * @param serverUrl - URL for which the gateway session is to be created
	 * @returns Promise<GatewaySession>
	 */
	public static async probeForForms(serverUrl: URL): Promise<GatewaySession> {
		const probeResponse = await DSAuth.getInstance().fetch(
			'HEAD',
			serverUrl.origin + gatewayConstants.FORMS_PROBE_PATH,
			{
				Accept: gatewayConstants.ACCEPT_AUTHENTICATE_RESPONSE,
				Host: serverUrl.hostname,
				'x-Citrix-Gateway': serverUrl.hostname,
				'X-Citrix-AM-LabelTypes': gatewayConstants.LABEL_TYPES,
				'X-Citrix-AM-CredentialTypes': gatewayConstants.CREDENTIAL_TYPES,
			},
			'',
			'',
			true,
			''
		);

		const serverInfo = new ServerInfo();
		serverInfo.gatewayInfo = new GatewayInfo();
		const contentTypeValue = HeaderParser.getInstance().extractHeaderValue(
			probeResponse.headers,
			'Content-Type'
		);

		if (
			probeResponse.statusCode === HttpStatusCodes.HTTP_STATUS_OK &&
			contentTypeValue?.includes(gatewayConstants.AUTHENTICATE_RESPONSE)
		) {
			serverInfo.serverType = StoreType.AdvancedGateway;
		} else if (probeResponse.statusCode === HttpStatusCodes.HTTP_STATUS_REDIRECT) {
			serverInfo.serverType = StoreType.BasicGateway;
			await getGatewayPasswordCountAndSelfDeclaredAuthType(serverUrl, serverInfo);
		}

		return new GatewaySession(serverUrl, probeResponse, serverInfo);
	}

	/**
	 * API to make an authenticated HTTP request via the current gateway if logged on,
	 * else logs on to the gateway and makes the request including the cookies required
	 * @param method - method of the HTTP request
	 * @param url - URL of the request
	 * @param headers - HTTP Headers of the request in JSON format
	 * @param body - body of the request
	 * @param referenceUri - referenceUri, against which the cookies will be stored, should be same for subsequent requests
	 * @param id - transaction ID for the request
	 * @returns Promise<HTTPFetchResponse>
	 */
	public async authenticatedFetch(
		method: string,
		url: string,
		headers: HeadersJson,
		body: string,
		referenceUri: string,
		allowCredUi: boolean,
		id?: string
	): Promise<HTTPFetchResponse> {
		try {
			if (
				(this.status === GatewaySessionStatus.LOGGED_OFF && allowCredUi) ||
				this.status === GatewaySessionStatus.NOT_INITIALIZED
			) {
				Logger.getInstance().write(
					logConstants.INFO,
					`Gateway session is logged off or not initialized, trying to login`,
					AotCategory.Info,
					`Gateway session is logged off or not initialized, trying to re-login`
				);

				this.authenticationData = {
					result: LogonResult.AccessDenied,
					authdata: new Map<string, string>(),
				};

				Logger.getInstance().writeAot(AotCategory.Info, `Logging on to gateway`);
				const result = await this.logOnToGateway();

				if (result == LogonResult.Success) {
					Logger.getInstance().writeAot(
						AotCategory.Info,
						`Gateway login completed successfully`
					);
					const discoverProbe = await DSAuth.getInstance().fetch(
						'GET',
						this.gatewayUrl.origin + gatewayConstants.DISCOVER_PROBE_PATH,
						{
							Connection: 'Close', // * closing connection adds overhead but is inevitable due to how gateway maintains connections
							Host: this.gatewayUrl.hostname,
							'X-Citrix-Gateway': this.gatewayUrl.hostname,
							Cookie: `${
								GatewayConstants.NSC_AAAC
							}=${this.authenticationData.authdata.get(GatewayConstants.NSC_AAAC)}`,
						},
						'',
						'',
						true,
						''
					);
					this.authenticationData.authdata.set(
						GatewayConstants.StorefrontURL,
						discoverProbe.body
					);
					this.directUrl = new URL(discoverProbe.body);
					this.status = GatewaySessionStatus.LOGGED_ON;
				} else {
					Logger.getInstance().write(
						logConstants.ERROR,
						'Error during authenticated fetch: '
					);
					Logger.getInstance().writeAot(
						AotCategory.Error,
						`Store addition failure - Logging on to gateway failed`
					);
					throw new GatewaySessionError(result);
				}
			}

			// Translating the URL using the rewrite rule fetched during gateway log-on
			const serverUrl = new URL(url);
			let directUrl = serverUrl;

			if (serverUrl.origin === this.gatewayUrl.origin) {
				directUrl = new URL(
					this.authenticationData.authdata.get(GatewayConstants.StorefrontURL) +
						serverUrl.pathname +
						serverUrl.search
				);
			}
			let translatedUrl = directUrl;

			const requireTranslateValue = HeaderParser.getInstance().extractHeaderValue(
				headers,
				GatewayConstants.RequireTranslate
			);
			if (requireTranslateValue !== 'false') {
				translatedUrl = translateUrlSFToGateway(
					this.gatewayUrl,
					directUrl,
					this.authenticationData.authdata.get(GatewayConstants.RewriteRule)
				);
			} else {
				delete headers[GatewayConstants.RequireTranslate];
				delete headers[GatewayConstants.RequireTranslate.toLowerCase()];
			}

			headers['Cookie'] = `${
				GatewayConstants.NSC_AAAC
			}=${this.authenticationData.authdata.get(GatewayConstants.NSC_AAAC)}`;

			let response = await DSAuth.getInstance().fetch(
				method,
				translatedUrl.toString(),
				headers,
				body,
				referenceUri,
				allowCredUi,
				id
			);

			// ! TODO - Check for invalid AAAC cookie in the header
			if (response.statusCode === HttpStatusCodes.HTTP_STATUS_REDIRECT) {
				Logger.getInstance().write(
					logConstants.INFO,
					`Received a redirect, trying to restore the NSC_AAAC cookie`,
					AotCategory.Info,
					`Received a redirect, trying to restore the gateway session`
				);

				this.status = GatewaySessionStatus.LOGGED_OFF;

				await CookieManager.getInstance().deleteSessionCookiesForReferenceUri(
					referenceUri
				);
				await CookieManager.getInstance().deleteSessionCookiesForReferenceUri(
					this.gatewayUrl.origin
				);

				if (!allowCredUi) {
					this.authenticationData.result = LogonResult.GatewaySessionExpired;
					throw new GatewaySessionError(LogonResult.GatewaySessionExpired);
				}

				this.authenticationData = {
					result: LogonResult.AccessDenied,
					authdata: new Map<string, string>(),
				};

				response = await this.authenticatedFetch(
					method,
					url,
					headers,
					body,
					referenceUri,
					allowCredUi
				);
			}

			return response;
		} catch (e) {
			Logger.getInstance().write(
				logConstants.ERROR,
				'Error during authenticated fetch: ' + e.message
			);
			throw e;
		}
	}

	/**
	 * Function that logs onto the gateway session, stores the authentication data and returns the logonResult
	 * @returns Promise<LogonResult>
	 */
	private async logOnToGateway(): Promise<LogonResult> {
		const auth = await this.gateway.logon();
		this.authenticationData = auth;
		return auth.result;
	}

	/**
	 * Function to remove the session, sending a GET request to cancel post-back url and invalidating the stored AAAC cookie
	 */
	public async delete(): Promise<void> {
		try {
			Logger.getInstance().writeAot(
				AotCategory.Info,
				`Removing gateway session - ${this.gatewayUrl}`
			);
			this.authenticationData.authdata.delete(GatewayConstants.NSC_AAAC);
			this.status = GatewaySessionStatus.LOGGED_OFF;
			await this.gateway.logoff();
			Logger.getInstance().writeAot(
				AotCategory.Info,
				`Gateway session removed and invalidated successfully`
			);
		} catch (e) {
			Logger.getInstance().write(
				logConstants.INFO,
				`Removing gateway session failed - ${this.gatewayUrl} - ${e.message}`,
				AotCategory.Warning,
				`Failed to remove gateway session - ${this.gatewayUrl}`
			);
		}
	}

	public getSessionCookie(): string {
		return this.authenticationData.authdata.get(GatewayConstants.NSC_AAAC);
	}

	public getTranslatedUrl(url: string, urlTranslationType: UrlTranslationType): string {
		const serverUrl = new URL(url);
		const directUrl = new URL(
			this.authenticationData.authdata.get(GatewayConstants.StorefrontURL) +
				serverUrl.pathname +
				serverUrl.search
		);

		if (urlTranslationType == UrlTranslationType.StorefrontToGateway) {
			return translateUrlSFToGateway(
				this.gatewayUrl,
				directUrl,
				this.authenticationData.authdata.get(GatewayConstants.RewriteRule)
			).href;
		} else if (urlTranslationType == UrlTranslationType.GatewayToStorefront) {
			return translateUrlGatewayToSF(
				serverUrl,
				new URL(directUrl.origin),
				this.authenticationData.authdata.get(GatewayConstants.RewriteRule)
			).href;
		}
	}

	public isSessionActive(): boolean {
		return this.status === GatewaySessionStatus.LOGGED_ON;
	}

	public getGatewayUrl(): URL {
		return this.gatewayUrl;
	}

	public getDirectUrl(): URL {
		return this.directUrl;
	}

	public setRoamingAccountsResponse(response: HTTPFetchResponse): void {
		this.roamingAccountsResponse = response;
	}

	public getRoamingAccountsResponse(): HTTPFetchResponse {
		return this.roamingAccountsResponse;
	}

	public getReferenceUri(): string {
		return this.referenceUri;
	}

	public setReferenceUri(referenceUri: string): void {
		this.referenceUri = referenceUri;
	}

	public getServerType(): StoreType {
		return this.serverType;
	}
}
